import axios from 'axios';

// En producción, la API está en la misma URL que el frontend
// En desarrollo, usa el proxy o localhost:5000
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 segundos
});

/**
 * URL segura para acceder a archivos de generated_documents/ via proxy autenticado.
 * En sesión autenticada no necesita firma; el proxy valida la cookie de sesión.
 */
export const getSecureFileUrl = (filePath) => {
  if (!filePath) return null;
  return `${API_BASE_URL}/factura_download.php?file=${encodeURIComponent(filePath)}`;
};

// Interceptor para manejar errores (sin log en producción)
api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Configuración global (modo mantenimiento) — GET público, POST solo admin
export const configuracionAPI = {
  getModoMantenimiento: () => api.get('/configuracion.php', { params: { clave: 'modo_mantenimiento' } }),
  setModoMantenimiento: (activo) => api.post('/configuracion.php', { clave: 'modo_mantenimiento', valor: activo ? '1' : '0' }),
};

// Usuarios
export const usuariosAPI = {
  getAll: (activos = null, rol = null) => {
    const params = {};
    if (activos !== null) params.activos = activos;
    if (rol) params.rol = rol;
    return api.get('/usuarios', { params });
  },
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.post(`/usuarios/${id}`, { ...data, _method: 'PUT' }),
  delete: (id) => api.post(`/usuarios/${id}`, { _method: 'DELETE' }),
  activate: (id) => api.post(`/usuarios/${id}`, { _method: 'PUT', activo: 1 }),
  uploadLogo: (userId, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('user_id', userId);
    return api.post('/upload_logo.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadFavicon: (userId, file) => {
    const formData = new FormData();
    formData.append('favicon', file);
    formData.append('user_id', userId);
    return api.post('/upload_favicon.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Demo coach – regenerar datos ficticios (solo admin)
export const demoSeedAPI = {
  regenerate: () => api.post('/demo_seed.php'),
};

// Backups
export const backupsAPI = {
  getAll: (limit = 50) => api.get('/backups', { params: { limit } }),
  getStats: () => api.get('/backups/stats'),
  create: () => api.post('/backups/create'),
  delete: (id) => api.delete(`/backups/${id}`),
  download: (id) => {
    const url = api.defaults.baseURL + `/backups/${id}/download`;
    window.open(url, '_blank');
  },
};

// Productos y servicios (facturación) – endpoints directos .php (evitan 500 del router)
export const productosServiciosAPI = {
  getAll: (activos = true) => api.get('/productos_servicios.php', { params: { activos } }),
  getById: (id) => api.get('/productos_servicios.php', { params: { id } }),
  create: (data) => api.post('/productos_servicios.php', data),
  update: (id, data) => api.post('/productos_servicios.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/productos_servicios.php', { _method: 'DELETE', id }),
};

// Tipos impositivos (IVA, etc.) por usuario – configuración facturación
export const tiposImpositivosAPI = {
  getAll: (activos = true) => api.get('/tipos_impositivos.php', { params: activos !== false ? { activos: 1 } : {} }),
  getById: (id) => api.get('/tipos_impositivos.php', { params: { id } }),
  create: (data) => api.post('/tipos_impositivos.php', data),
  update: (id, data) => api.post('/tipos_impositivos.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/tipos_impositivos.php', { _method: 'DELETE', id }),
};

// Plantillas de email (factura / presupuesto / recordatorio de sesión)
export const plantillasEmailAPI = {
  getAll: () => api.get('/plantillas_email.php'),
  getByTipo: (tipo) => api.get('/plantillas_email.php', { params: { tipo } }),
  save: (tipo, data) => api.post('/plantillas_email.php', { tipo, ...data }),
};

// Etiquetas de técnicas/herramientas/ejercicios (por coach)
export const etiquetasHerramientasAPI = {
  getAll: () => api.get('/etiquetas_herramientas.php'),
  create: (data) => api.post('/etiquetas_herramientas.php', data),
  update: (id, data) => api.post('/etiquetas_herramientas.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/etiquetas_herramientas.php', { _method: 'DELETE', id }),
};

// Facturas – endpoints directos .php (evitan 500 del router)
export const facturasAPI = {
  getAll: (filters = {}) => api.get('/facturas.php', { params: filters }),
  getById: (id) => api.get('/facturas.php', { params: { id } }),
  getHistorial: (id) => api.get('/facturas.php', { params: { id, historial: 1 } }),
  create: (data) => api.post('/facturas.php', data),
  update: (id, data) => api.post('/facturas.php', { ...data, _method: 'PUT', id }),
  updateEstado: (id, estado) => api.post('/facturas.php', { _method: 'PATCH', id, estado }),
  delete: (id) => api.post('/facturas.php', { _method: 'DELETE', id }),
  restaurar: (id, auditoriaId) => api.post('/facturas.php', { _method: 'RESTAURAR', id, auditoria_id: auditoriaId }),
  /** Abre la factura en HTML para imprimir / guardar como PDF */
  getPdfUrl: (id) => `${api.defaults.baseURL}/factura_pdf.php?id=${id}`,
  /** Descarga factura electrónica UE (UBL 2.1 / EN 16931) en XML */
  getEinvoiceUrl: (id) => `${api.defaults.baseURL}/factura_einvoice.php?id=${id}`,
  /** Duplicar factura (crea nueva con mismo cliente y líneas) */
  duplicate: (id) => api.post('/facturas.php', { _method: 'DUPLICATE', id }),
  /** Descarga varias facturas en un ZIP (cada una como HTML). ids: number[] */
  downloadZip: (ids) => {
    if (!ids || ids.length === 0) return;
    const params = new URLSearchParams();
    ids.forEach((id) => params.append('ids[]', id));
    window.open(`${api.defaults.baseURL}/facturas_zip.php?${params.toString()}`, '_blank');
  },
};

// Facturas recurrentes (plantillas para generar facturas automáticas)
export const facturasRecurrentesAPI = {
  getAll: (activo = null) => api.get('/facturas_recurrentes.php', { params: activo !== null ? { activo: activo ? 1 : 0 } : {} }),
  getById: (id) => api.get('/facturas_recurrentes.php', { params: { id } }),
  create: (data) => api.post('/facturas_recurrentes.php', data),
  update: (id, data) => api.post('/facturas_recurrentes.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/facturas_recurrentes.php', { _method: 'DELETE', id }),
  generar: (id) => api.post('/facturas_recurrentes.php', { _method: 'GENERAR', id }),
  runJob: () => api.get('/generar_facturas_recurrentes.php', { timeout: 15000 }),
};

// Pagos (facturación)
export const pagosAPI = {
  getByFactura: (facturaId) => api.get('/pagos.php', { params: { factura_id: facturaId } }),
  getTransacciones: (filters = {}) => api.get('/pagos.php', { params: filters }),
  create: (data) => api.post('/pagos.php', data),
  delete: (id, facturaId) => api.post('/pagos.php', { _method: 'DELETE', id, factura_id: facturaId }),
  retry: (id, facturaId) => api.post('/pagos.php', { _method: 'RETRY', id, factura_id: facturaId }),
  sendReciboEmail: (id, email) => api.post('/pagos.php', { accion: 'enviar_recibo_email', id, email }),
  getReciboUrl: (id, download = true) => `${api.defaults.baseURL}/pago_recibo.php?id=${id}${download ? '&download=1' : ''}`,
};

// Presupuestos (facturación)
export const presupuestosAPI = {
  getAll: (filters = {}) => api.get('/presupuestos.php', { params: filters }),
  getById: (id) => api.get('/presupuestos.php', { params: { id } }),
  getHistorial: (id) => api.get('/presupuestos.php', { params: { id, historial: 1 } }),
  create: (data) => api.post('/presupuestos.php', data),
  update: (id, data) => api.post('/presupuestos.php', { ...data, _method: 'PUT', id }),
  updateEstado: (id, estado) => api.post('/presupuestos.php', { _method: 'PATCH', id, estado }),
  delete: (id) => api.post('/presupuestos.php', { _method: 'DELETE', id }),
  restaurar: (id, auditoriaId) => api.post('/presupuestos.php', { _method: 'RESTAURAR', id, auditoria_id: auditoriaId }),
  convertToFactura: (id) => api.post('/presupuestos.php', { _method: 'CONVERT', id }),
  /** Abre el presupuesto en HTML para imprimir / guardar como PDF */
  getPdfUrl: (id) => `${api.defaults.baseURL}/presupuesto_pdf.php?id=${id}`,
  /** Duplicar presupuesto (crea nuevo con mismo cliente y líneas) */
  duplicate: (id) => api.post('/presupuestos.php', { _method: 'DUPLICATE', id }),
};

// Códigos de descuento y promociones
export const codigosDescuentoAPI = {
  getAll: (todos = false) => api.get('/codigos_descuento.php', { params: todos ? { todos: 1 } : {} }),
  getById: (id) => api.get('/codigos_descuento.php', { params: { id } }),
  validar: (codigo, subtotal) => api.get('/codigos_descuento.php', { params: { validar: codigo, subtotal } }),
  create: (data) => api.post('/codigos_descuento.php', data),
  update: (id, data) => api.post('/codigos_descuento.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/codigos_descuento.php', { _method: 'DELETE', id }),
};

// Descuentos por volumen
export const descuentosVolumenAPI = {
  getAll: () => api.get('/descuentos_volumen.php'),
  getById: (id) => api.get('/descuentos_volumen.php', { params: { id } }),
  create: (data) => api.post('/descuentos_volumen.php', data),
  update: (id, data) => api.post('/descuentos_volumen.php', { ...data, _method: 'PUT', id }),
  delete: (id) => api.post('/descuentos_volumen.php', { _method: 'DELETE', id }),
};

// Estadísticas de facturación
export const facturacionStatsAPI = {
  getStats: () => api.get('/facturacion_stats.php'),
};

// Histórico / dashboard de actividad
export const historicoAPI = {
  getData: (filters = 12) => {
    const params = typeof filters === 'number' ? { meses: filters } : (filters || {});
    return api.get('/historico.php', { params });
  },
};

// Archivos de cliente
export const archivosClienteAPI = {
  getByCliente: (clienteId, categoria) => {
    const params = { cliente_id: clienteId };
    if (categoria && categoria !== 'todos') params.categoria = categoria;
    return api.get('/archivos_cliente.php', { params });
  },
  upload: (clienteId, file, categoria, notas) => {
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('cliente_id', clienteId);
    formData.append('categoria', categoria || 'otro');
    if (notas) formData.append('notas', notas);
    return api.post('/archivos_cliente.php', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  download: (id) => {
    const baseUrl = process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');
    window.open(`${baseUrl}/archivos_cliente.php?id=${id}&action=download`, '_blank');
  },
  delete: (id) => api.post('/archivos_cliente.php', { _method: 'DELETE', id }),
};

// Disponibilidad / reservas (coach config – autenticado)
export const disponibilidadAPI = {
  get: () => api.get('/disponibilidad'),
  saveSemanal: (slots) => api.post('/disponibilidad', { semanal: slots }),
  addExcepcion: (data) => api.post('/disponibilidad', { excepcion: data, ...data }),
  deleteSlot: (id, type) => api.delete('/disponibilidad', { params: { id, type } }),
  updateConfig: (data) => api.post('/disponibilidad', { ...data, _method: 'PUT' }),
};

// Reservas públicas (sin auth – fetch directo a reservas_publicas.php)
const reservasPublicasBase = () =>
  (process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api')) +
  '/reservas_publicas.php';

const fetchWithTimeout = (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
};

export const reservasPublicasAPI = {
  getCoachInfo: (username) =>
    fetchWithTimeout(`${reservasPublicasBase()}?usuario=${encodeURIComponent(username)}`).then(async (r) => {
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 404) {
          const err = new Error(body.error || 'Coach no encontrado o reservas no habilitadas');
          err.code = 'NOT_FOUND';
          throw err;
        }
        throw new Error(body.detalle || body.error || 'Error al cargar información del coach');
      }
      return r.json();
    }),
  getSlots: (username, fecha) =>
    fetchWithTimeout(
      `${reservasPublicasBase()}?usuario=${encodeURIComponent(username)}&fecha=${encodeURIComponent(fecha)}`
    ).then((r) => {
      if (!r.ok) throw new Error('Error al cargar horarios');
      return r.json();
    }),
  createBooking: (data) =>
    fetchWithTimeout(reservasPublicasBase(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).then(async (r) => {
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || 'Error al reservar');
      return body;
    }),
};

export const recordatoriosAPI = {
  getRecordatorios: () => api.get('/recordatorios.php'),
  run: () => api.get('/recordatorios_automaticos.php', { timeout: 15000 }),
};

// Email
export const emailAPI = {
  send: (tipo, id, email, mensajePersonalizado = '') => api.post('/email.php', {
    tipo,
    id,
    email,
    mensaje_personalizado: mensajePersonalizado,
  }),
};

export const oportunidadesAPI = {
  getAll: (filters = {}) => api.get('/oportunidades.php', { params: filters }),
  getById: (id) => api.get(`/oportunidades.php?id=${id}`),
  create: (data) => api.post('/oportunidades.php', data),
  update: (id, data) => api.post('/oportunidades.php', { ...data, id, _method: 'PUT' }),
  updateEtapa: (id, etapa, motivoPerdida = null) => api.post('/oportunidades.php', { id, etapa, motivo_perdida: motivoPerdida, _method: 'PATCH' }),
  delete: (id) => api.post('/oportunidades.php', { id, _method: 'DELETE' }),
  getStats: () => api.get('/oportunidades.php?stats=1'),
};

export const actividadesAPI = {
  getByOportunidad: (oportunidadId) => api.get(`/actividades.php?oportunidad_id=${oportunidadId}`),
  create: (data) => api.post('/actividades.php', data),
  update: (id, data) => api.post('/actividades.php', { ...data, id, _method: 'PUT' }),
  complete: (id, resultado = null) => api.post('/actividades.php', { id, resultado, _method: 'COMPLETE' }),
  delete: (id) => api.post('/actividades.php', { id, _method: 'DELETE' }),
};

export const tareasAPI = {
  getAll: (filters = {}) => api.get('/tareas.php', { params: filters }),
  getProximas: (dias = 7) => api.get('/tareas.php?proximas=1&dias=' + dias),
  getById: (id) => api.get(`/tareas.php?id=${id}`),
  create: (data) => api.post('/tareas.php', data),
  update: (id, data) => api.post('/tareas.php', { ...data, id, _method: 'PUT' }),
  complete: (id, notas = null) => api.post('/tareas.php', { id, notas_completado: notas, _method: 'COMPLETE' }),
  delete: (id) => api.post('/tareas.php', { id, _method: 'DELETE' }),
};

export const interaccionesAPI = {
  getAll: (filters = {}) => api.get('/interacciones.php', { params: filters }),
  getByCliente: (clienteId, limit = 100) => api.get('/interacciones.php', { params: { cliente_id: clienteId, limit } }),
  getById: (id) => api.get(`/interacciones.php?id=${id}`),
  create: (data) => api.post('/interacciones.php', data),
  update: (id, data) => api.post('/interacciones.php', { ...data, id, _method: 'PUT' }),
  delete: (id) => api.post('/interacciones.php', { id, _method: 'DELETE' }),
};

export const reportesAPI = {
  getFacturacion: (filters = {}) => api.get('/reportes.php', { params: { tipo: 'facturacion', ...filters } }),
  getOportunidades: (filters = {}) => api.get('/reportes.php', { params: { tipo: 'oportunidades', ...filters } }),
  exportCSV: (tipo, filters = {}) => {
    const params = new URLSearchParams({ tipo, formato: 'csv', ...filters });
    window.open(`${api.defaults.baseURL}/reportes.php?${params.toString()}`, '_blank');
  },
  /** Abre la vista HTML del reporte para imprimir / guardar como PDF */
  exportPDF: (tipo, filters = {}) => {
    const params = new URLSearchParams({ tipo, formato: 'pdf', ...filters });
    window.open(`${api.defaults.baseURL}/reportes.php?${params.toString()}`, '_blank', 'noopener,width=900,height=700');
  },
};

export const planesAPI = {
  getAll: (activo = null) => api.get('/planes.php', { params: activo !== null ? { activo } : {} }),
  getById: (id) => api.get(`/planes.php?id=${id}`),
  create: (data) => api.post('/planes.php', data),
  update: (id, data) => api.post('/planes.php', { ...data, id, _method: 'PUT' }),
  // hard=false -> desactivar; hard=true -> borrar definitivamente
  delete: (id, hard = false) => api.post('/planes.php', { id, _method: 'DELETE', ...(hard ? { hard: true } : {}) }),
};

export const suscripcionesAPI = {
  getAll: (estado = null) => api.get('/suscripciones.php', { params: estado ? { estado } : {} }),
  getById: (id) => api.get(`/suscripciones.php?id=${id}`),
  getByUsuario: (usuarioId) => api.get(`/suscripciones.php?usuario_id=${usuarioId}`),
  create: (data) => api.post('/suscripciones.php', data),
  update: (id, data) => api.post('/suscripciones.php', { ...data, id, _method: 'PUT' }),
};

export const pagosUsuariosAPI = {
  getAll: (estado = null, usuarioId = null) => {
    const params = {};
    if (estado) params.estado = estado;
    if (usuarioId) params.usuario_id = usuarioId;
    return api.get('/pagos_usuarios.php', { params });
  },
  getById: (id) => api.get(`/pagos_usuarios.php?id=${id}`),
  getByUsuario: (usuarioId, estado = null) => {
    const params = { usuario_id: usuarioId };
    if (estado) params.estado = estado;
    return api.get('/pagos_usuarios.php', { params });
  },
  create: (data) => api.post('/pagos_usuarios.php', data),
  marcarPagado: (id, data = {}) => api.post('/pagos_usuarios.php', { accion: 'marcar_pagado', id, ...data }),
  generarPagos: (suscripcionId, cantidad = 12) => api.post('/pagos_usuarios.php', { accion: 'generar_pagos', suscripcion_id: suscripcionId, cantidad }),
  generarFactura: (id, numero_factura) => api.post('/pagos_usuarios.php', { accion: 'generar_factura', id, numero_factura }),
  editarNumeroFactura: (id, numero_factura) => api.post('/pagos_usuarios.php', { accion: 'editar_numero_factura', id, numero_factura }),
  eliminarFactura: (id) => api.post('/pagos_usuarios.php', { accion: 'eliminar_factura', id }),
  delete: (id) => api.post('/pagos_usuarios.php', { id, _method: 'DELETE' }),
};

// Clientes
export const clientesAPI = {
  getAll: (activos = true) => api.get('/clientes', { params: { activos } }),
  getById: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.post(`/clientes/${id}`, { ...data, _method: 'PUT' }),
  delete: (id, hard = false) => {
    const data = { _method: 'DELETE' };
    if (hard) {
      data.hard = true;
    }
    return api.post(`/clientes/${id}`, data);
  },
};

export const portalClienteAPI = {
  getStatus: (clienteId) => api.get('/portal_manage.php', { params: { cliente_id: clienteId } }),
  toggle: (clienteId) => api.post('/portal_manage.php', { action: 'toggle', cliente_id: clienteId }),
  regenerateToken: (clienteId) => api.post('/portal_manage.php', { action: 'regenerate_token', cliente_id: clienteId }),
};

export const integracionesConfigAPI = {
  get: () => api.get('/integraciones_config.php'),
  generateIcalToken: () => api.post('/integraciones_config.php', { action: 'generate_ical_token' }),
  revokeIcalToken: () => api.post('/integraciones_config.php', { action: 'revoke_ical_token' }),
};

export const auditoriaGeneralAPI = {
  getAll: (params = {}) => api.get('/auditoria_general.php', { params }),
};

// Consultas
export const consultasAPI = {
  getAll: (clienteId = null) => {
    const params = clienteId ? { cliente_id: clienteId } : {};
    return api.get('/consultas', { params });
  },
  getById: (id) => api.get(`/consultas/${id}`),
  create: (data) => api.post('/consultas', data),
  // IONOS puede bloquear PUT, usamos POST con _method=PUT
  update: (id, data) => api.post(`/consultas/${id}`, { ...data, _method: 'PUT' }),
  // IONOS bloquea DELETE para /consultas, usar POST + _method=DELETE
  delete: (id) => api.post(`/consultas/${id}`, { _method: 'DELETE' }),
};

// Citas
export const citasAPI = {
  getAll: (clienteId = null, fechaInicio = null, fechaFin = null) => {
    const params = {};
    if (clienteId) params.cliente_id = clienteId;
    if (fechaInicio) params.fecha_inicio = fechaInicio;
    if (fechaFin) params.fecha_fin = fechaFin;
    return api.get('/citas', { params });
  },
  getById: (id) => api.get(`/citas/${id}`),
  create: (data) => api.post('/citas', data),
  // IONOS puede bloquear PUT, usamos POST con _method=PUT
  update: (id, data) => api.post(`/citas/${id}`, { ...data, _method: 'PUT' }),
  // IONOS bloquea DELETE para /citas, usar POST + _method=DELETE
  delete: (id) => api.post(`/citas/${id}`, { _method: 'DELETE' }),
};

// Autenticación
export const authAPI = {
  login: (username, password) => api.post('/auth.php?action=login', { username, password }),
  logout: () => api.get('/auth.php?action=logout'),
  check: () => api.get('/auth.php?action=check'),
};

// Documentos
export const documentosAPI = {
  getPlantillas: (activas = true) => api.get('/plantillas', { params: { activas } }),
  getPlantillaById: (id) => api.get(`/plantillas/${id}`),
  createPlantilla: (data, file = null) => {
    if (file) {
      // Si hay archivo, usar FormData
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('nombre', data.nombre);
      if (data.descripcion) formData.append('descripcion', data.descripcion);
      formData.append('campos_personalizables', JSON.stringify(data.campos_personalizables || []));
      formData.append('activa', data.activa !== false);
      
      return api.post('/plantillas', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Sin archivo, usar JSON normal
      return api.post('/plantillas', data);
    }
  },
  updatePlantilla: (id, data, file = null) => {
    // IONOS puede bloquear PUT, así que usamos POST con _method=PUT
    if (file) {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('nombre', data.nombre);
      if (data.descripcion) formData.append('descripcion', data.descripcion);
      formData.append('campos_personalizables', JSON.stringify(data.campos_personalizables || []));
      formData.append('activa', data.activa !== false);
      formData.append('_method', 'PUT');

      return api.post(`/plantillas/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Sin archivo, enviar JSON con _method=PUT
      return api.post(`/plantillas/${id}`, { ...data, _method: 'PUT' });
    }
  },
  // deletePlantilla: IONOS bloquea DELETE, usamos POST con _method=DELETE
  // hard=false -> desactivar; hard=true -> borrar definitivamente
  deletePlantilla: (id, hard = false) => {
    const data = { _method: 'DELETE' };
    if (hard) {
      data.hard = true;
    }
    return api.post(`/plantillas/${id}`, data);
  },
  getDocumentos: (clienteId = null) => {
    const params = clienteId ? { cliente_id: clienteId } : {};
    return api.get('/documentos', { params });
  },
  getDocumentoById: (id) => api.get(`/documentos/${id}`),
  generarDocumento: (data) => api.post('/documentos/generar', data),
  downloadDocumento: (id) => api.get(`/documentos/${id}/download`, { responseType: 'blob' }),
  downloadDocumentoPDF: (id) => api.get(`/documentos/${id}/download?format=pdf`, { responseType: 'blob' }),
  deleteDocumento: (id) => {
    // IONOS puede bloquear DELETE, usar POST como alternativa
    return api.post(`/documentos/${id}`, { _method: 'DELETE' });
  },
};

// Búsqueda global
export const searchAPI = {
  search: (query, type = 'all') => {
    return api.get('/search.php', { params: { q: query, type } });
  },
};

// Integraciones: tokens de API y webhooks
export const apiTokensAPI = {
  getAll: () => api.get('/api_tokens.php'),
  create: (nombre) => api.post('/api_tokens.php', { nombre }),
  delete: (id) => api.post('/api_tokens.php', { _method: 'DELETE', id }),
};

export const webhooksAPI = {
  getAll: () => api.get('/webhooks.php'),
  create: (data) => api.post('/webhooks.php', data),
  update: (id, data) => api.post('/webhooks.php', { _method: 'PUT', id, ...data }),
  delete: (id) => api.post('/webhooks.php', { _method: 'DELETE', id }),
};

export const pushAPI = {
  getVapidKey: () => api.get('/push_subscriptions.php', { params: { action: 'vapid_public_key' } }),
  subscribe: (subscription) => api.post('/push_subscriptions.php', { subscription }),
  unsubscribe: (endpoint) => api.post('/push_subscriptions.php', { _method: 'DELETE', endpoint }),
  getSubscriptions: () => api.get('/push_subscriptions.php'),
};

export const notificacionesAPI = {
  getAll: (limit = 30, offset = 0) => api.get('/notificaciones.php', { params: { limit, offset } }),
  countNoLeidas: () => api.get('/notificaciones.php', { params: { action: 'count' } }),
  marcarLeida: (id) => api.post('/notificaciones.php', { action: 'marcar_leida', id }),
  marcarTodasLeidas: () => api.post('/notificaciones.php', { action: 'marcar_todas_leidas' }),
  eliminar: (id) => api.post('/notificaciones.php', { _method: 'DELETE', id }),
};

export default api;

